import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { getMeshFileType } from './fileTypes';
// NOTE: generatePreview (utils/meshUtils) is dynamic-imported at its call site
// below — a static import here pulls three + the STL/OBJ loader stack into the
// landing chunk (this hook is in TextAreaChat's graph), defeating the Lazy3D
// boundary. It only runs on a user mesh upload.

type ToastFn = ReturnType<typeof useToast>['toast'];

interface UseAttachmentUploadsArgs {
  conversation: {
    id: string;
    user_id: string;
  };
  toast: ToastFn;
}

interface UploadArgs {
  file: File;
  id: string;
}

interface UseAttachmentUploadsResult {
  uploadImageAsync: (args: UploadArgs) => Promise<string>;
  uploadMeshAsync: (args: UploadArgs) => Promise<string | undefined>;
}

// Image + mesh upload mutations, extracted verbatim from TextAreaChat. These
// are a contiguous pair of `useMutation` hooks; calling this hook in their
// place preserves the root's observable hook order. The mutations close over
// `conversation` (for the storage path + owner check) and surface failures
// via the shared toast. `addItems` and the drag/paste/drop ingest flow stay
// in the root — they are tightly coupled to the root's attachment state
// setters and the generation/preview pipeline.
export function useAttachmentUploads({
  conversation,
  toast,
}: UseAttachmentUploadsArgs): UseAttachmentUploadsResult {
  const { mutateAsync: uploadImageAsync } = useMutation({
    mutationFn: async ({ file, id }: { file: File; id: string }) => {
      // Use the live session user ID — not conversation.user_id — so the storage
      // path always matches auth.uid() in the RLS policy, even when a god-mode
      // placeholder or stale localStorage session is still in React state.
      const {
        data: { session: liveSession },
      } = await supabase.auth.getSession();
      const realUserId = liveSession?.user?.id ?? conversation.user_id;

      const { error } = await supabase.storage
        .from('images')
        .upload(`${realUserId}/${conversation.id}/${id}`, file);

      if (error) throw error;

      const reader = new FileReader();
      const urlPromise = new Promise((resolve) => {
        reader.onload = () => {
          resolve(reader.result as string);
        };
      });
      reader.readAsDataURL(file);
      const url = (await urlPromise) as string;

      return url;
    },
    onError: (error) => {
      console.error('[uploadImageAsync]', error);
      toast({
        title: 'Error',
        description: 'Failed to upload image',
        variant: 'destructive',
      });
    },
  });

  const { mutateAsync: uploadMeshAsync } = useMutation({
    mutationFn: async ({ file, id }: { file: File; id: string }) => {
      const fileExtension = getMeshFileType(file.name);

      const {
        data: { session: liveSession },
      } = await supabase.auth.getSession();
      const realUserId = liveSession?.user?.id ?? conversation.user_id;

      const { error } = await supabase.storage
        .from('meshes')
        .upload(
          `${realUserId}/${conversation.id}/${id}.${fileExtension}`,
          file,
        );

      if (error) throw error;

      // Check if preview exists in storage
      const previewPath = `${realUserId}/${conversation.id}/preview-${id}`;

      const { data } = await supabase.storage
        .from('images')
        .createSignedUrl(previewPath, 60 * 60); // 1 hour expiry

      if (data && data.signedUrl) {
        return data.signedUrl;
      }

      // If preview doesn't exist, generate it with the correct file type
      const { generatePreview } = await import('@/utils/meshUtils');
      const preview = await generatePreview(file, fileExtension);

      // Only upload if the current user is the conversation owner
      if (realUserId && realUserId === conversation.user_id) {
        // Convert data URL to Blob
        const response = await fetch(preview);
        const blob = await response.blob();

        // Save the preview to storage
        const { error: uploadError } = await supabase.storage
          .from('images')
          .upload(previewPath, blob, {
            contentType: 'image/png',
            upsert: true,
          });

        if (uploadError) {
          console.error('Error uploading preview:', uploadError);
          return preview; // Return the preview anyway even if upload fails
        }

        // Get the signed URL of the uploaded preview
        const { data } = await supabase.storage
          .from('images')
          .createSignedUrl(previewPath, 60 * 60); // 1 hour expiry
        return data?.signedUrl;
      }

      // If not the owner, just return the generated preview
      return preview;
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to upload mesh',
        variant: 'destructive',
      });
    },
  });

  return { uploadImageAsync, uploadMeshAsync };
}
